import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { AuthApiService, UserData } from '@angular-workspace/services';
import { FormatService } from '../../services/format.service';
import { ToasterService } from '../../../services/toaster.service';
import { KeycloakService } from 'keycloak-angular';
import { LogoutService } from '../../../services/logout.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ConfirmationDialogComponent],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit {
  @Input() username?: string;
  @Input() displayName?: string;
  @Input() accountCreatedDate?: string;
  @Input() firstName?: string;
  @Input() lastName?: string;
  @Input() email?: string;
  @Input() emailVerified?: boolean;
  @Input() organisationName?: string;
  organisationStatus: 'pending' | 'granted' | null = null;

  loading = false;
  error: string | null = null;

  loadingCredentials = false;
  userRoles: string[] = [];
  showChangePasswordModal = false;
  changePasswordForm!: FormGroup;
  loadingPasswordChange = false;
  loadingDeleteAccount = false;
  loadingRevokePrivacy = false;
  loadingRevokeTerms = false;
  emailCopied = false;

  private keycloak = inject(KeycloakService);
  private logoutService = inject(LogoutService);
  private googleAnalytics = inject(GoogleAnalyticsService);

  constructor(
    private authApiService: AuthApiService,
    private formatService: FormatService,
    private toasterService: ToasterService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initializeChangePasswordForm();
    this.loadUserData();
    this.checkPasswordChangeRedirect();
  }

  /**
   * Check if user was redirected back after changing password in Keycloak.
   * If so, show success toaster and log them out.
   */
  private checkPasswordChangeRedirect(): void {
    // Check URL params directly to avoid subscription timing issues
    const urlParams = new URLSearchParams(window.location.search);
    const passwordChanged = urlParams.get('passwordChanged');
    
    if (passwordChanged === 'true') {
      console.log('[Profile] Password change detected, initiating logout...');
      
      // Remove the query parameter from URL without reloading
      const url = new URL(window.location.href);
      url.searchParams.delete('passwordChanged');
      window.history.replaceState({}, '', url.toString());

      // Show success toaster with longer duration (5 seconds)
      this.toasterService.success('Password changed successfully. You will be logged out shortly.', 5000);

      // Logout user after a short delay to let user see the toaster
      setTimeout(() => {
        console.log('[Profile] Executing logout...');
        this.logoutService.logout(window.location.origin)
          .then(() => {
            console.log('[Profile] Logout successful');
          })
          .catch((error) => {
            console.error('[Profile] Error during logout:', error);
            // Force redirect if logout fails
            window.location.href = '/';
          });
      }, 2500);
    }
  }

  initializeChangePasswordForm(): void {
    this.changePasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, this.passwordValidator]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  /**
   * Custom validator for password with all criteria
   */
  passwordValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const value = control.value;
    const errors: ValidationErrors = {};

    // Check length (8-20 characters)
    if (value.length < 8 || value.length > 20) {
      errors['invalidLength'] = true;
    }

    // Check for lowercase
    if (!/[a-z]/.test(value)) {
      errors['noLowercase'] = true;
    }

    // Check for uppercase
    if (!/[A-Z]/.test(value)) {
      errors['noUppercase'] = true;
    }

    // Check for numbers
    if (!/[0-9]/.test(value)) {
      errors['noNumber'] = true;
    }

    // Check for special characters
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
      errors['noSpecialChar'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  /**
   * Check if password meets all criteria
   */
  get passwordCriteria(): {
    length: boolean;
    lowercase: boolean;
    uppercase: boolean;
    number: boolean;
    specialChar: boolean;
  } {
    const password = this.newPasswordControl?.value || '';
    
    return {
      length: password.length >= 8 && password.length <= 20,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  }

  /**
   * Check if all password criteria are met
   */
  get allPasswordCriteriaMet(): boolean {
    const criteria = this.passwordCriteria;
    return criteria.length && criteria.lowercase && criteria.uppercase && 
           criteria.number && criteria.specialChar;
  }

  /**
   * Custom validator to check if passwords match
   */
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');
    
    if (!newPassword || !confirmPassword) {
      return null;
    }
    
    return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  async loadUserData(): Promise<void> {
    this.loading = true;
    this.error = null;
    
    try {
      const userData: UserData = await this.authApiService.getUserInfo();
      this.populateUserData(userData);
    } catch (error) {
      console.error('Error loading user data:', error);
      this.error = 'Failed to load user information. Please try again later.';
    } finally {
      this.loading = false;
    }
  }

  populateUserData(userData: UserData): void {
    // The API response has a 'result' object containing the actual user data
    const result = userData['result'] || userData;
    
    // Map API response fields as specified:
    // firstName -> result.familyName
    // lastName -> result.givenName
    // email -> result.email
    this.firstName = result['givenName'] || '';
    this.lastName = result['familyName'] || '';
    this.email = result['email'] || '';

    // Construct display name
    const fullName = result['name'] || 
                     (this.firstName && this.lastName ? `${this.firstName} ${this.lastName}` : '') ||
                     '';
    
    this.displayName = fullName;
    this.username = result.name || 
                    result['preferred_username'] || 
                    this.email ||
                    this.displayName ||
                    '';

    this.emailVerified = result['emailVerified'] !== undefined 
                         ? result['emailVerified'] 
                         : (result['email_verified'] !== undefined ? result['email_verified'] : false);

    // Format account created date
    if (result['createdAt'] || result['created_at'] || result['createdAtTimestamp']) {
      const createdAt = result['createdAt'] || result['created_at'] || result['createdAtTimestamp'];
      this.accountCreatedDate = this.formatService.formatDateOnly(createdAt);
    }

    // Organization name and status from result.organisation
    const organisation = result['organisation'] || result['organization'] || {};
    const orgName = organisation['organisationName'] || 
                   organisation['organizationName'] ||
                   organisation['orgName'] ||
                   organisation['name'] ||
                   '';
    
    this.organisationName = orgName || 'Not affiliated';
    
    // Extract organisation status
    if (organisation && organisation.status) {
      this.organisationStatus = organisation.status === 'pending' || organisation.status === 'granted' ? organisation.status : null;
    } else {
      this.organisationStatus = null;
    }

    // Extract user roles
    this.userRoles = result['roles'] || [];
  }

  /**
   * Check if user has an organisation (registered or joined)
   * @returns true if user has an organisation, false otherwise
   */
  get hasOrganisation(): boolean {
    return !!this.organisationName && 
           this.organisationName !== 'Not affiliated' && 
           this.organisationName.trim() !== '';
  }

  /**
   * Check if user has cos_admin role
   * @returns true if user has cos_admin role, false otherwise
   */
  get isCosAdmin(): boolean {
    return this.userRoles.includes('cos_admin') || this.userRoles.includes('cos-admin');
  }

  /**
   * Check if user has admin role (including cos_admin)
   * @returns true if user has admin role, false otherwise
   */
  get isAdmin(): boolean {
    return this.userRoles.includes('admin') || 
           this.userRoles.includes('cos_admin') || 
           this.userRoles.includes('cos-admin') ||
           this.userRoles.includes('platform-admin') ||
           this.userRoles.includes('platform_admin');
  }

  showDeleteConfirmDialog = false;
  showRevokePrivacyDialog = false;
  showRevokeTermsDialog = false;

  async handleGetCredentials(): Promise<void> {
    this.loadingCredentials = true;
    try {
      const response = await this.authApiService.getClientCredentials();
      
      // Extract clientId and clientSecret from response
      // Handle both direct response and nested result object
      const result = response['result'] || response;
      const clientId = result['clientId'] || result['client_id'];
      const clientSecret = result['clientSecret'] || result['client_secret'];
      
      if (clientId && clientSecret) {
        // Create JSON object with clientId and clientSecret
        const credentialsData = {
          clientId: clientId,
          clientSecret: clientSecret
        };
        
        // Download as JSON file
        this.downloadJsonFile(credentialsData, 'client-credentials.json');
        this.googleAnalytics.trackProfileGetCredentials();
        this.toasterService.success('Credentials downloaded successfully');
      } else {
        this.toasterService.show('error', 'Client credentials not found in response');
      }
    } catch (error) {
      console.error('Error getting credentials:', error);
      this.toasterService.show('error', 'Failed to retrieve credentials. Please try again.');
    } finally {
      this.loadingCredentials = false;
    }
  }

  /**
   * Download data as a JSON file
   * @param data - The data object to download
   * @param filename - The filename for the downloaded file
   */
  private downloadJsonFile(data: any, filename: string): void {
    try {
      // Convert data to JSON string with pretty formatting
      const jsonString = JSON.stringify(data, null, 2);
      
      // Create a Blob with the JSON data
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the object URL to free up memory
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading JSON file:', error);
      this.toasterService.show('error', 'Failed to download credentials file');
    }
  }

  /**
   * Redirect to Keycloak's password update flow.
   * Uses Keycloak's built-in UPDATE_PASSWORD action which redirects the user
   * to the password change page and returns them to the app after completion.
   * Forces re-authentication before allowing password change for security.
   */
  async handleChangePassword(): Promise<void> {
    try {
      // Build redirect URL with passwordChanged query parameter
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('passwordChanged', 'true');
      
      // Use Keycloak's login with UPDATE_PASSWORD action
      // prompt: 'login' forces user to re-authenticate before changing password
      // This adds an extra security layer - user must prove they know current password
      await this.keycloak.login({
        action: 'UPDATE_PASSWORD',
        prompt: 'login',
        redirectUri: currentUrl.toString()
      });
    } catch (error) {
      console.error('Error redirecting to Keycloak password change:', error);
      this.toasterService.error('Failed to open password change page. Please try again.');
    }
  }

  closeChangePasswordModal(): void {
    this.showChangePasswordModal = false;
    this.changePasswordForm.reset();
  }

  async onSubmitChangePassword(): Promise<void> {
    if (this.changePasswordForm.invalid || !this.allPasswordCriteriaMet) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.changePasswordForm.controls).forEach(key => {
        this.changePasswordForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loadingPasswordChange = true;
    try {
      const { newPassword } = this.changePasswordForm.value;
      
      // Call the change password API
      await this.authApiService.changePassword(newPassword);
      this.googleAnalytics.trackProfileChangePassword();
      this.toasterService.success('Password changed successfully. You will be logged out shortly.');
      // Close the modal first
      this.closeChangePasswordModal();
      
      // Logout the user after a short delay to show the success message
      setTimeout(async () => {
        try {
          await this.logoutService.logout(window.location.origin);
        } catch (error) {
          console.error('Error during logout:', error);
          window.location.href = '/';
        }
      }, 1500);
    } catch (error) {
      console.error('Error changing password:', error);
      this.toasterService.show('error', 'Failed to change password. Please try again.');
      this.loadingPasswordChange = false;
    }
  }

  get newPasswordControl() {
    return this.changePasswordForm.get('newPassword');
  }

  get confirmPasswordControl() {
    return this.changePasswordForm.get('confirmPassword');
  }

  get passwordsMatch(): boolean {
    return !this.changePasswordForm.hasError('passwordMismatch') || 
           !this.confirmPasswordControl?.touched;
  }

  viewPrivacyPolicy(): void {
    this.router.navigate(['/privacy-policy']);
  }

  viewTermsOfService(): void {
    this.router.navigate(['/terms-of-service']);
  }

  handleRevokePrivacyConsent(): void {
    this.showRevokePrivacyDialog = true;
  }

  handleRevokeTermsConsent(): void {
    this.showRevokeTermsDialog = true;
  }

  handleDeleteAccount(): void {
    this.showDeleteConfirmDialog = true;
  }

  async onConfirmDeleteAccount(): Promise<void> {
    this.loadingDeleteAccount = true;
    try {
      // Call the delete user API
      await this.authApiService.deleteUser();
      this.googleAnalytics.trackProfileDeleteAccount();
      // Show success message
      this.toasterService.success('Your account has been deleted successfully. You will be logged out shortly.');
      // Close the dialog
      this.showDeleteConfirmDialog = false;
      this.loadingDeleteAccount = false;
      
      // Logout the user after a short delay to show the success message
      setTimeout(async () => {
        try {
          await this.logoutService.logout(window.location.origin);
        } catch (error) {
          console.error('Error during logout:', error);
          window.location.href = '/';
        }
      }, 1500);
    } catch (error) {
      console.error('Error deleting account:', error);
      this.toasterService.show('error', 'Failed to delete account. Please try again.');
      this.loadingDeleteAccount = false;
    }
  }

  async onConfirmRevokePrivacy(): Promise<void> {
    this.loadingRevokePrivacy = true;
    try {
      // Call the delete user API (same as Delete My Account)
      await this.authApiService.deleteUser();
      
      // Show success message
      this.toasterService.success('Your account has been deleted successfully. You will be logged out shortly.');
      
      // Close the dialog
      this.showRevokePrivacyDialog = false;
      this.loadingRevokePrivacy = false;
      
      // Logout the user after a short delay to show the success message
      setTimeout(async () => {
        try {
          await this.logoutService.logout(window.location.origin);
        } catch (error) {
          console.error('Error during logout:', error);
          window.location.href = '/';
        }
      }, 1500);
    } catch (error) {
      console.error('Error revoking privacy consent:', error);
      this.toasterService.show('error', 'Failed to revoke consent. Please try again.');
      this.loadingRevokePrivacy = false;
    }
  }

  async onConfirmRevokeTerms(): Promise<void> {
    this.loadingRevokeTerms = true;
    try {
      // Call the delete user API (same as Delete My Account)
      await this.authApiService.deleteUser();
      
      // Show success message
      this.toasterService.success('Your account has been deleted successfully. You will be logged out shortly.');
      
      // Close the dialog
      this.showRevokeTermsDialog = false;
      this.loadingRevokeTerms = false;
      
      // Logout the user after a short delay to show the success message
      setTimeout(async () => {
        try {
          await this.logoutService.logout(window.location.origin);
        } catch (error) {
          console.error('Error during logout:', error);
          window.location.href = '/';
        }
      }, 1500);
    } catch (error) {
      console.error('Error revoking terms consent:', error);
      this.toasterService.show('error', 'Failed to revoke consent. Please try again.');
      this.loadingRevokeTerms = false;
    }
  }

  getDeleteAccountMessage(): string {
    return 'Are you sure you want to delete your account? This action cannot be undone.';
  }

  getRevokeConsentMessage(): string {
    return 'Are you sure you want to revoke this consent? This action cannot be undone.';
  }

  handleLinkClick(): void {
    // TODO: Implement link click handler
    console.log('Link clicked');
  }

  /**
   * Copy email address to clipboard (identical to dataset detail copy functionality)
   */
  copyEmailToClipboard(): void {
    if (!this.email) {
      return;
    }

    navigator.clipboard.writeText(this.email).then(() => {
      this.emailCopied = true;
      setTimeout(() => {
        this.emailCopied = false;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy email:', err);
    });
  }

  /**
   * Get user initials from first name and last name
   * @returns string with first letter of first name and first letter of last name
   */
  getUserInitials(): string {
    let initials = '';
    
    // Try to get initials from firstName and lastName
    if (this.firstName && this.firstName.trim()) {
      initials += this.firstName.trim().charAt(0).toUpperCase();
    }
    
    if (this.lastName && this.lastName.trim()) {
      initials += this.lastName.trim().charAt(0).toUpperCase();
    }
    
    // If we have initials from first and last name, return them
    if (initials.length >= 2) {
      return initials.substring(0, 2);
    }
    
    // Fallback to username/email if first and last name are not available
    if (this.username && this.username.trim()) {
      const usernameParts = this.username.trim().split(/[\s@._-]+/);
      if (usernameParts.length >= 2) {
        return (usernameParts[0].charAt(0) + usernameParts[1].charAt(0)).toUpperCase().substring(0, 2);
      } else if (usernameParts[0]) {
        return usernameParts[0].charAt(0).toUpperCase();
      }
    }
    
    // Fallback to email if username is not available
    if (this.email && this.email.trim()) {
      const emailParts = this.email.trim().split('@');
      if (emailParts[0]) {
        const localPart = emailParts[0];
        const localParts = localPart.split(/[._-]+/);
        if (localParts.length >= 2) {
          return (localParts[0].charAt(0) + localParts[1].charAt(0)).toUpperCase().substring(0, 2);
        } else {
          return localPart.charAt(0).toUpperCase();
        }
      }
    }
    
    // Final fallback
    return initials || 'U';
  }
}
